import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { CRow, CCol, CCard, CCardHeader, CCardBody, CButton } from "@coreui/react";
import dayjs from "dayjs";
// Вид как у react-fast-timeline 1.3 на странице экскаватора, но стили и классы свои (ttl-*):
// не зависим от того, какая сборка библиотеки стоит в node_modules
import "./TruckTimelineSection.css";

// Шкала самосвала: активности IMU, погрузки и рейсы.
// Кладется в src/core/pages/CameraDetailPage/components/TruckTimelineSection.jsx
//
// Разметка - как у react-fast-timeline (обертка, ось, дорожка, блоки), стили - в TruckTimelineSection.css,
// сегменты раскладывает этот компонент. Так все три дорожки считаются от одного вида
// и всегда совпадают: зум или сдвиг любой дорожки двигает остальные.
//
// Вид (начало и конец) лежит во внешнем viewStore: шкалы на странице и в окне видео подписаны
// на него обе. Прокрутка перерисовывает только эту секцию, не страницу, и не чаще раза за кадр.
// Время кадра видео (playheadStore) двигает маркер напрямую через style, без рендера.

const MIN_VIEW_MS = 1000;
const DRAG_THRESHOLD_PX = 3;
const CLICK_MAX_MS = 350;
const HIT_TOLERANCE_PX = 3;
// Сегменты уже этого сливаются в группы по сетке времени (как LOD у библиотеки)
const MIN_BLOCK_PX = 3;
const TICK_MIN_PX = 95;
// Маркер и перерывы - от верха первой дорожки, как в CameraTimelineSection
const OVERLAY_TOP = "25px";

// Шаги сетки оси - как в библиотеке
const AXIS_STEPS = [
  10, 20, 50, 100, 200, 500, 1e3, 2e3, 5e3, 1e4, 15e3, 3e4, 6e4, 12e4, 3e5, 6e5, 9e5, 18e5, 36e5, 72e5, 108e5,
  216e5, 432e5, 864e5, 1728e5, 6048e5, 2592e6, 7776e6, 31536e6,
];

// Подписи оси - как в библиотеке (локаль браузера)
const fmtTick = (ms, step) => {
  const d = new Date(ms);
  if (step < 1e3) {
    const hms = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    return `${hms}.${String(d.getMilliseconds()).padStart(3, "0")}`;
  }
  if (step < 6e4) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  if (step < 864e5) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (step < 2592e6) {
    const date = d.toLocaleDateString([], { day: "2-digit", month: "2-digit" });
    return `${date} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
  }
  return d.toLocaleDateString();
};

const buildTicks = (view, width) => {
  const span = view.end - view.start;
  const target = span / Math.max(2, Math.floor(width / TICK_MIN_PX));
  const step = AXIS_STEPS.find((s) => s >= target) || AXIS_STEPS[AXIS_STEPS.length - 1];
  const ticks = [];
  let lastPct = null;
  for (let tm = Math.floor(view.start / step) * step; tm <= view.end + step; tm += step) {
    if (tm < view.start - step) continue;
    const pct = ((tm - view.start) / span) * 100;
    if (lastPct === null || ((pct - lastPct) / 100) * width >= TICK_MIN_PX) {
      ticks.push({ time: tm, pct, label: fmtTick(tm, step) });
      lastPct = pct;
    }
  }
  return ticks;
};

// Цвет текста на плашке - как в библиотеке
const labelColorCache = new Map();
const labelColor = (color) => {
  let c = labelColorCache.get(color);
  if (c) return c;
  c = "#ffffff";
  let rgb = null;
  if (color && color.startsWith("#")) {
    let h = color.slice(1);
    if (h.length === 3) h = h.split("").map((x) => x + x).join("");
    rgb = [parseInt(h.substr(0, 2), 16), parseInt(h.substr(2, 2), 16), parseInt(h.substr(4, 2), 16)];
  } else if (color && color.startsWith("rgb")) {
    const m = color.match(/\d+/g);
    if (m && m.length >= 3) rgb = m.slice(0, 3).map(Number);
  }
  if (rgb) c = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000 >= 128 ? "#000000" : "#ffffff";
  labelColorCache.set(color, c);
  return c;
};

const isOverlay = (item) => item.originalItem?.class_id === -2 || item.originalItem?.class_id === -3;

// Индекс первого элемента, который заканчивается позже t. Элементы дорожки отсортированы
// и не пересекаются, поэтому концы тоже отсортированы.
const firstEndingAfter = (items, t) => {
  let lo = 0;
  let hi = items.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (items[mid].end <= t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
};

const itemNear = (items, t, tolMs) => {
  const i = firstEndingAfter(items, t - tolMs);
  return i < items.length && items[i].start <= t + tolMs ? items[i] : null;
};

const clampView = (start, end, range) => {
  const full = range.end - range.start;
  const span = Math.min(Math.max(end - start, Math.min(MIN_VIEW_MS, full)), full);
  const s = Math.max(range.start, Math.min(start, range.end - span));
  return { start: s, end: s + span };
};

const fmtDuration = (ms) => {
  const total = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h) return `${h} ч ${m} мин`;
  if (m) return `${m} мин ${s} с`;
  return `${s} с`;
};

// Подсказка считается один раз на сегмент, а не на каждом кадре прокрутки
const titleCache = new WeakMap();
const itemTitle = (item) => {
  let title = titleCache.get(item);
  if (!title) {
    title =
      `${item.label || ""}\n` +
      `${dayjs(item.start).format("DD.MM HH:mm:ss")} – ${dayjs(item.end).format("HH:mm:ss")}\n` +
      `${fmtDuration(item.end - item.start)}`;
    titleCache.set(item, title);
  }
  return title;
};

// Видимые блоки дорожки. Широкие сегменты - по одному. Узкие (уже MIN_BLOCK_PX) сливаются
// по сетке времени, привязанной к абсолютному времени, а не к краю экрана: при сдвиге группы
// не перескакивают. Цвет группы - цвет самого длинного сегмента в ней.
const buildBlocks = (items, view, width, selectedId) => {
  const span = view.end - view.start;
  if (!(span > 0) || !items.length) return [];
  const k = width / span;
  const binMs = 2 ** Math.floor(Math.log2(Math.max(1, MIN_BLOCK_PX / k)));
  const toBlock = (start, end) => {
    const s = Math.max(start, view.start);
    const e = Math.min(end, view.end);
    return { left: ((s - view.start) / span) * 100, width: ((e - s) / span) * 100, px: (e - s) * k };
  };

  const blocks = [];
  let group = null;
  const flush = () => {
    if (!group) return;
    blocks.push({
      key: `g-${group.bin}-${group.first}`,
      group: true,
      count: group.count,
      color: group.color,
      ...toBlock(group.start, group.end),
    });
    group = null;
  };

  for (let i = firstEndingAfter(items, view.start); i < items.length && items[i].start < view.end; i += 1) {
    const it = items[i];
    const dur = it.end - it.start;
    if (dur * k >= MIN_BLOCK_PX) {
      flush();
      blocks.push({
        key: `s-${it.id}`,
        item: it,
        color: it.color,
        selected: selectedId != null && it.id === selectedId,
        ...toBlock(it.start, it.end),
      });
      continue;
    }
    const bin = Math.floor(it.start / binMs);
    if (group && group.bin === bin && it.start - group.end < binMs * 0.8) {
      group.end = Math.max(group.end, it.end);
      group.count += 1;
      if (dur > group.maxDur) {
        group.maxDur = dur;
        group.color = it.color;
      }
    } else {
      flush();
      group = { bin, first: it.id, start: it.start, end: it.end, count: 1, maxDur: dur, color: it.color };
    }
  }
  flush();
  return blocks;
};

const Block = ({ block }) => {
  const { item } = block;
  const single = !block.group;
  const textColor = labelColor(block.color);
  const className = `${single ? "ttl-item" : "ttl-cluster"}${block.selected ? " ttl-item-selected" : ""}`;
  return (
    <div
      className={className}
      style={{ left: `${block.left}%`, width: `${block.width}%`, minWidth: "2px", backgroundColor: block.color }}
      title={single ? itemTitle(item) : `Сегментов: ${block.count} — приблизьте`}
    >
      {block.selected && (
        <span className="ttl-selected-badge" title="Выбрано">
          ✔
        </span>
      )}
      {single && block.px >= 16 && (
        <span
          className="ttl-label"
          style={{
            color: textColor,
            padding: block.px < 25 ? "0 1px" : "0 5px",
            textOverflow: block.px < 35 ? "clip" : "ellipsis",
            fontWeight: block.selected ? "bold" : textColor === "#000000" ? "600" : "normal",
          }}
        >
          {item.label}
        </span>
      )}
    </div>
  );
};

Block.propTypes = { block: PropTypes.object.isRequired };

const TruckTimelineSection = ({
  t,
  dateRangeForVis,
  timelineDataForVis = [],
  timelineLoadingsForVis = [],
  timelineTripsForVis = [],
  viewStore,
  playheadStore,
  showPlayhead,
  onSeek,
  selectedTripId,
  onTripSelect,
}) => {
  const rootRef = useRef(null);
  const markerRef = useRef(null);
  const dragRef = useRef(null);
  const [width, setWidth] = useState(1000);

  const range = useMemo(
    () => (dateRangeForVis ? { start: dateRangeForVis.start.getTime(), end: dateRangeForVis.end.getTime() } : null),
    [dateRangeForVis]
  );

  // Вид из общего хранилища; рендер не чаще раза за кадр
  const [storeView, setStoreView] = useState(() => (viewStore ? viewStore.get() : null));
  useEffect(() => {
    if (!viewStore) return undefined;
    let raf = 0;
    const apply = () => {
      raf = 0;
      setStoreView(viewStore.get());
    };
    const unsubscribe = viewStore.subscribe(() => {
      if (!raf) raf = requestAnimationFrame(apply);
    });
    apply();
    return () => {
      unsubscribe();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [viewStore]);
  const view = storeView || range;

  const overlays = useMemo(() => timelineDataForVis.filter(isOverlay), [timelineDataForVis]);
  const lanes = useMemo(
    () => [
      {
        key: "activity",
        title: "Активности IMU",
        items: timelineDataForVis.filter((it) => !isOverlay(it)).sort((a, b) => a.start - b.start),
      },
      {
        key: "loading",
        title: `Погрузки: ${timelineLoadingsForVis.length}`,
        items: [...timelineLoadingsForVis].sort((a, b) => a.start - b.start),
      },
      {
        key: "trips",
        title: `Рейсы: ${timelineTripsForVis.length}`,
        items: [...timelineTripsForVis].sort((a, b) => a.start - b.start),
      },
    ],
    [timelineDataForVis, timelineLoadingsForVis, timelineTripsForVis]
  );

  // Свежие значения для обработчиков событий
  const stateRef = useRef({});
  stateRef.current = { range, lanes, onSeek, onTripSelect, selectedTripId, view, showPlayhead };

  const getView = useCallback(() => (viewStore && viewStore.get()) || stateRef.current.range, [viewStore]);

  const setView = useCallback(
    (start, end) => {
      const r = stateRef.current.range;
      if (r && viewStore) viewStore.set(clampView(start, end, r));
    },
    [viewStore]
  );

  const zoomAt = useCallback(
    (factor, pct = 0.5) => {
      const v = getView();
      if (!v) return;
      const span = v.end - v.start;
      const newSpan = span * factor;
      const anchor = v.start + span * pct;
      setView(anchor - newSpan * pct, anchor - newSpan * pct + newSpan);
    },
    [getView, setView]
  );

  // Ширина дорожек (рамка обертки - по 1 px с каждой стороны)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = (w) => setWidth(Math.max(1, Math.floor(w) - 2));
    const observer = new ResizeObserver((entries) => measure(entries[0].contentRect.width));
    observer.observe(el);
    measure(el.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, [range]);

  // Колесо - масштаб относительно курсора, только над дорожкой
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      const container = e.target.closest ? e.target.closest(".ttl-container") : null;
      if (!container || !getView()) return;
      e.preventDefault();
      const rect = container.getBoundingClientRect();
      zoomAt(e.deltaY > 0 ? 1.3 : 0.77, (e.clientX - rect.left) / rect.width);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [range, getView, zoomAt]);

  // Маркер кадра видео - только в окне с видео, по тому виду, что сейчас нарисован
  const updateMarker = useCallback(() => {
    const el = markerRef.current;
    if (!el) return;
    const { view: v, showPlayhead: show } = stateRef.current;
    const ms = show && playheadStore ? playheadStore.get() : null;
    if (ms == null || !v) {
      el.style.display = "none";
      return;
    }
    const pct = ((ms - v.start) / (v.end - v.start)) * 100;
    if (pct < 0 || pct > 100) {
      el.style.display = "none";
      return;
    }
    el.style.display = "block";
    el.style.left = `${pct}%`;
  }, [playheadStore]);

  useEffect(() => {
    updateMarker();
    return showPlayhead && playheadStore ? playheadStore.subscribe(updateMarker) : undefined;
  }, [showPlayhead, playheadStore, updateMarker]);

  useLayoutEffect(() => {
    updateMarker();
  }, [view, updateMarker]);

  const hitTest = (container, laneKey, clientX) => {
    const v = getView();
    const lane = stateRef.current.lanes.find((l) => l.key === laneKey);
    if (!v || !lane) return { time: null, item: null };
    const rect = container.getBoundingClientRect();
    const span = v.end - v.start;
    const time = v.start + ((clientX - rect.left) / rect.width) * span;
    const tolMs = (span / Math.max(1, rect.width)) * HIT_TOLERANCE_PX;
    return { time, item: itemNear(lane.items, time, tolMs) };
  };

  const handlePointerDown = (e) => {
    if (e.button !== 0 || !getView()) return;
    dragRef.current = { x: e.clientX, view: getView(), moved: false, t0: performance.now() };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.x;
    if (!drag.moved && Math.abs(dx) > DRAG_THRESHOLD_PX) drag.moved = true;
    if (!drag.moved) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const span = drag.view.end - drag.view.start;
    const shift = -(dx / rect.width) * span;
    setView(drag.view.start + shift, drag.view.end + shift);
  };

  const handlePointerUp = (e) => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    if (!drag || drag.moved || performance.now() - drag.t0 > CLICK_MAX_MS) return;

    // Клик по рейсу - выбрать его (трек на карте), повторный клик - снять выбор
    const laneKey = e.currentTarget.dataset.lane;
    const { onTripSelect: select, selectedTripId: selected } = stateRef.current;
    if (laneKey !== "trips" || !select) return;
    const { item } = hitTest(e.currentTarget, laneKey, e.clientX);
    const id = item ? item.originalItem.id : null;
    select(id != null && id === selected ? null : id);
  };

  const handleDoubleClick = (e) => {
    const seek = stateRef.current.onSeek;
    if (typeof seek !== "function") return;
    const { time, item } = hitTest(e.currentTarget, e.currentTarget.dataset.lane, e.clientX);
    if (time != null) seek({ exactTime: time, item: item || undefined });
  };

  const ticks = useMemo(() => (view ? buildTicks(view, width) : []), [view, width]);
  const selectedTripKey = selectedTripId != null ? `trip_${selectedTripId}` : null;
  const laneBlocks = useMemo(
    () =>
      view
        ? lanes.map((lane) => buildBlocks(lane.items, view, width, lane.key === "trips" ? selectedTripKey : null))
        : [],
    [lanes, view, width, selectedTripKey]
  );

  const renderOverlays = () => {
    if (!view) return null;
    const totalViewMs = view.end - view.start;
    return overlays.map((brk) => {
      const leftPct = ((brk.start - view.start) / totalViewMs) * 100;
      const rightPct = ((brk.end - view.start) / totalViewMs) * 100;
      const renderLeft = Math.max(0, leftPct);
      const renderRight = Math.min(100, rightPct);
      const renderWidth = renderRight - renderLeft;
      if (renderWidth <= 0) return null;

      const title = brk.originalItem.class_id === -2 ? "ТО / Выходной" : "Перерыв";
      return (
        <div
          key={brk.id}
          title={title}
          style={{
            position: "absolute",
            top: OVERLAY_TOP,
            bottom: 0,
            left: `${renderLeft}%`,
            width: `${renderWidth}%`,
            pointerEvents: "none",
            zIndex: 5,
            background:
              "repeating-linear-gradient(-45deg, transparent, transparent 5px, rgba(0,0,0,0.1) 5px, rgba(0,0,0,0.1) 10px)",
            backgroundColor: "rgba(200, 200, 200, 0.25)",
            borderLeft: renderLeft > 0 ? "1px dashed #666" : "none",
            borderRight: renderRight < 100 ? "1px dashed #666" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {renderWidth > 3 && (
            <span
              style={{
                color: "#555",
                fontWeight: "bold",
                fontSize: "0.85rem",
                textShadow: "1px 1px 0 #fff",
                whiteSpace: "nowrap",
                transform: "rotate(-90deg)",
              }}
            >
              {title}
            </span>
          )}
        </div>
      );
    });
  };

  const renderLane = (lane, idx) => {
    const selectable = lane.key === "trips" && Boolean(onTripSelect);
    return (
      <div className={`ttl-wrapper${selectable ? " ttl-selectable" : ""}`}>
        <div className="ttl-controls">
          <button type="button" className="ttl-btn" onClick={() => zoomAt(1.5)} title="Отдалить">
            -
          </button>
          <button type="button" className="ttl-btn" onClick={() => zoomAt(0.5)} title="Приблизить">
            +
          </button>
        </div>
        <div
          className="ttl-container"
          data-lane={lane.key}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onDoubleClick={handleDoubleClick}
        >
          <div className="ttl-axis">
            {ticks.map((tk) => (
              <div key={tk.time} className="ttl-tick" style={{ left: `${tk.pct}%` }}>
                {tk.label}
              </div>
            ))}
          </div>
          <div className="ttl-track">
            {(laneBlocks[idx] || []).map((block) => (
              <Block key={block.key} block={block} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <CRow className="mb-4">
      <CCol xs={12}>
        <CCard className="shadow-sm border-0">
          <CCardHeader className="bg-white py-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <span className="fw-bold">{t("analysis.timeline_title", "Временная шкала работы")}</span>
            <div className="d-flex align-items-center gap-3">
              <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                Колесо или +/− — масштаб · перетаскивание — сдвиг · двойной клик — перемотка видео
                {onTripSelect ? " · клик по рейсу — трек на карте" : ""}
              </span>
              <CButton
                size="sm"
                color="secondary"
                variant="outline"
                onClick={() => range && viewStore && viewStore.set({ ...range })}
              >
                Весь период
              </CButton>
            </div>
          </CCardHeader>

          <CCardBody>
            {range && view ? (
              <div ref={rootRef} className="position-relative">
                {lanes.map((lane, idx) => (
                  <React.Fragment key={lane.key}>
                    <div className={`fw-bold text-muted small mb-2${idx > 0 ? " mt-4" : ""}`}>{lane.title}</div>
                    {renderLane(lane, idx)}
                  </React.Fragment>
                ))}

                {renderOverlays()}

                {/* Маркер кадра видео: линия и кружок сверху, как у экскаватора.
                    Двигается напрямую через style, без рендера */}
                <div
                  ref={markerRef}
                  style={{
                    display: "none",
                    position: "absolute",
                    top: OVERLAY_TOP,
                    bottom: 0,
                    width: "2px",
                    marginLeft: "-1px",
                    backgroundColor: "#e55353",
                    zIndex: 100,
                    pointerEvents: "none",
                    boxShadow: "0 0 6px rgba(229, 83, 83, 0.8)",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-6px",
                      left: "-5px",
                      width: "12px",
                      height: "12px",
                      backgroundColor: "#e55353",
                      borderRadius: "50%",
                      border: "2px solid white",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="text-center p-3 text-muted">
                Выберите период и нажмите &quot;Получить аналитику&quot;
              </div>
            )}
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

TruckTimelineSection.propTypes = {
  t: PropTypes.func.isRequired,
  dateRangeForVis: PropTypes.object,
  timelineDataForVis: PropTypes.array,
  timelineLoadingsForVis: PropTypes.array,
  timelineTripsForVis: PropTypes.array,
  viewStore: PropTypes.object,
  playheadStore: PropTypes.object,
  showPlayhead: PropTypes.bool,
  onSeek: PropTypes.func,
  selectedTripId: PropTypes.number,
  onTripSelect: PropTypes.func,
};

export default React.memo(TruckTimelineSection);
